import { useEffect, useState } from "react";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import { GET_SECTIONS } from "../../../../queries";
import { useQuery } from "@apollo/client";

interface SectionType {
  id: string;
  name: string;
  legacyId: string;
}

interface SectionsQueryData {
  crmSections: {
    nodes: SectionType[];
  };
}

interface SectionAutocompleteProps {
  value: string | null;
  onChange: (value: string, sectionSlug: string) => void;
  onReset: () => void;
}

const SectionAutocomplete = ({ value, onChange, onReset }: SectionAutocompleteProps) => {
  const [query, setQuery] = useState<string>(value || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sectionSearch, setSectionSearch] = useState<string>("");
  const { data: sectionData, loading: sectionLoading } = useQuery<SectionsQueryData>(GET_SECTIONS, {
    variables: { input: { search: sectionSearch } },
    skip: sectionSearch === ""
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setSectionSearch(debouncedSearchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!value) {
      setQuery("");
      setDebouncedSearchTerm("");
      setSectionSearch("");
    }
  }, [value]);

  function selectSection(selectedValue: SectionType) {
    const sectionSlug = selectedValue.legacyId === "PGA" ? "national" : `section-${selectedValue.legacyId}`;
    onChange(selectedValue.name, sectionSlug);
    setQuery(selectedValue.name);
    setDebouncedSearchTerm(selectedValue.name);
  }

  return (
    <Autocomplete
      label="Section"
      query={query}
      loadingResults={sectionLoading}
      data={sectionData?.crmSections?.nodes || []}
      onChangeText={(string) => {
        setQuery(string);
        setDebouncedSearchTerm(string);
      }}
      reset={() => {
        onReset();
        setQuery("");
        setDebouncedSearchTerm("");
        setSectionSearch("");
      }}
      onSelect={selectSection}
      renderItem={(data) => (
        <div className="flex items-center border-bottom">
          <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
            <Icon name="domain" size={24} />
          </div>
          <div>
            <div className="text-body-large">{data.name}</div>
          </div>
        </div>
      )}
    />
  );
};

export default SectionAutocomplete;
